import {
  makeRedeemCode,
  pickWeighted,
} from "@/components/marketing";
import { useEffect, useRef, useState } from "react";
import type { WheelPrize } from "../../../data";

type SpinPhase = "idle" | "spinning" | "offer" | "done";

// Matches the wheel's CSS transition (4s) plus a little slack.
const SPIN_MS = 4100;

interface UseSpinWheelInput {
  prizes: WheelPrize[];
}

// Spin & Win state: spin picks a weighted prize and turns the wheel so that
// slice stops under the pointer, then the phone shows the offer; redeem
// shows a code; "spin again" goes back to the start. With reduced motion the
// wheel jumps straight to the prize.
export function useSpinWheel({ prizes }: UseSpinWheelInput) {
  const initialState = {
    phase: "idle" as SpinPhase,
    rotation: 0,
    prizeIndex: 0,
    code: "",
  };
  const [state, setState] = useState(initialState);
  const timerRef = useRef<number | undefined>(undefined);
  const hasInteractedRef = useRef(false);
  const spinButtonRef = useRef<HTMLButtonElement>(null);
  const redeemButtonRef = useRef<HTMLButtonElement>(null);
  const againButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const clearSpinTimer = () => window.clearTimeout(timerRef.current);
    return clearSpinTimer;
  }, []);

  // The button that was pressed disappears with each phase, so move focus to
  // the next action (only after the visitor has used the demo).
  useEffect(() => {
    function moveFocus() {
      if (!hasInteractedRef.current) return;
      const target = {
        idle: spinButtonRef,
        spinning: null,
        offer: redeemButtonRef,
        done: againButtonRef,
      }[state.phase];
      target?.current?.focus();
    }
    moveFocus();
  }, [state.phase]);

  const spin = () => {
    if (state.phase === "spinning") return;
    hasInteractedRef.current = true;
    const prizeIndex = pickWeighted(prizes);
    const slice = 360 / prizes.length;
    // Whole turns past the current angle, minus the prize slice's centre,
    // leaves that slice under the pointer at the top.
    const rotation =
      (Math.ceil(state.rotation / 360) + 5) * 360 -
      (prizeIndex * slice + slice / 2);
    const isReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    setState((state) => ({
      ...state,
      rotation,
      prizeIndex,
      phase: isReduced ? "offer" : "spinning",
    }));
    if (isReduced) return;
    timerRef.current = window.setTimeout(
      () => setState((state) => ({ ...state, phase: "offer" })),
      SPIN_MS,
    );
  };

  const redeem = () =>
    setState((state) => ({ ...state, phase: "done", code: makeRedeemCode() }));

  const spinAgain = () => setState((state) => ({ ...state, phase: "idle" }));

  return {
    phase: state.phase,
    rotation: state.rotation,
    prize: prizes[state.prizeIndex],
    code: state.code,
    spin,
    redeem,
    spinAgain,
    spinButtonRef,
    redeemButtonRef,
    againButtonRef,
  };
}
